import { useState, useEffect, useRef } from 'react';
import { convertMarkdownToHTML } from '../../utils/convertMarkdownToHTML';
import MessageActions from '../message-actions/MessageActions';

const TypingMessage = ({
    scrollToBottom,
    message,
    chatId,
    typingSpeed = 3000,
    updatedAt
}: {
    scrollToBottom: () => void,
    message: string,
    chatId?: string,
    typingSpeed: number,
    updatedAt: string
}) => {
    const [arrayHtml, setArrayHtml] = useState<string[][]>();
    const [showActions, setShowActions] = useState(false);
    const containerRef = useRef<HTMLDivElement>(null);

    function splitTextPreservingHtml(text: string, maxLineLength = 80): string[] {
        const cleanedText = text
            .replace(/<br\s*\/?>/gi, '\n')
            .replace(/\n{3,}/g, '\n\n')
            .trim();

        const lines: string[] = [];
        let currentLine = '';
        let inTag = false;
        let tagBuffer = '';
        let visible = 0;
        let anchorDepth = 0;
        let lastSafeHtmlLen: number | null = null;
        let lastSafeVisible: number | null = null;

        const pushLine = (sliceLen?: number, sliceVisible?: number) => {
            if (sliceLen != null) {
                const head = currentLine.slice(0, sliceLen);
                if (head) lines.push(head);
                currentLine = currentLine.slice(sliceLen);
                visible = Math.max(0, visible - (sliceVisible ?? 0));
            } else {
                const s = currentLine.trim();
                if (s) lines.push(s);
                currentLine = '';
                visible = 0;
            }
            lastSafeHtmlLen = null;
            lastSafeVisible = null;
        };

        for (let i = 0; i < cleanedText.length; i++) {
            const ch = cleanedText[i];

            if (ch === '<') {
                inTag = true;
                tagBuffer = '<';
                continue;
            }

            if (ch === '>' && inTag) {
                tagBuffer += '>';
                inTag = false;

                const lower = tagBuffer.toLowerCase();

                if (/^<(li|p)[\s>]/.test(lower)) {
                    if (currentLine.trim()) pushLine();
                    currentLine += tagBuffer;
                    tagBuffer = '';
                    continue;
                }

                if (lower.startsWith('</li')) {
                    currentLine += tagBuffer;
                    pushLine(); 
                    tagBuffer = '';
                    continue;
                }

                if (/^<a[\s>]/.test(lower)) {
                    anchorDepth++;
                } else if (lower.startsWith('</a')) {
                    anchorDepth = Math.max(0, anchorDepth - 1);
                }

                currentLine += tagBuffer;
                tagBuffer = '';
                continue;
            }

            if (inTag) {
                tagBuffer += ch;
                continue;
            }

            if (ch === '\n') {
                if (currentLine.trim()) pushLine();
                continue;
            }

            currentLine += ch;
            visible += 1;

            if (ch === ' ' && anchorDepth === 0) {
                lastSafeHtmlLen = currentLine.length;
                lastSafeVisible = visible;
            }

            if (visible > maxLineLength && lastSafeHtmlLen != null) {
                pushLine(lastSafeHtmlLen, lastSafeVisible ?? 0);
            }
        }

        if (currentLine.trim()) pushLine();

        return lines;
    }

    useEffect(() => {
        const chunks = convertMarkdownToHTML(message);
        let fullOutputArray: string[][] = [];
        let charIndex = 0;

        setShowActions(false);

        const typeNext = () => {
            const lines = chunks
                .split(/\n+/)
                .filter(chunk => chunk.trim() !== '')
                .map(chunk => splitTextPreservingHtml(chunk));

            if (charIndex < lines.length) {
                fullOutputArray = [...fullOutputArray, lines[charIndex]];
                setArrayHtml(fullOutputArray);
                charIndex++;
                scrollToBottom();
                setTimeout(typeNext, typingSpeed);
            } else {
                setShowActions(true);
                scrollToBottom();
            }
        };

        typeNext();
    }, [message]);

    return (
        <div className="bot-message" ref={containerRef}>
            <p className="whitespace-pre-wrap break-words">
                {arrayHtml?.flat().map((html, index) => (
                    <span
                        key={index}
                        style={{ animationDelay: `${index * 200}ms` }}
                        className="fade-word"
                        dangerouslySetInnerHTML={{ __html: html }}
                    />
                ))}
            </p>

            {chatId && showActions && (
                <MessageActions chatId={chatId} message={message} topicId={chatId} updatedAt={updatedAt} />
            )}
        </div>
    );

};

export default TypingMessage;
